export const parseFlag = (value) => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true
  return false
}

export const parsePrice = (value) => {
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

export const isProductUnavailable = (product) => {
  if (!product) return true
  const status = product.stock_status
  return status === 0 || status === '0'
}

export const hasVariantOptions = (product) => {
  const options = product?.options
  return Array.isArray(options) && options.length > 0
}

export const hasRequiredOptions = (product) => {
  if (!hasVariantOptions(product)) return false
  return product.options.some((opt) => parseFlag(opt.required))
}

export const getInventoryMap = (inventory) => {
  if (!inventory || Array.isArray(inventory)) return {}
  return inventory
}

export const buildInventoryKey = (variantIds) => {
  const sorted = [...variantIds].map(Number).filter((id) => id > 0).sort((a, b) => a - b)
  if (!sorted.length) return null
  return `[${sorted.join(',')}]`
}

export const getInventoryEntry = (inventory, variantIds) => {
  const map = getInventoryMap(inventory)
  if (!variantIds?.length) return null

  const key = buildInventoryKey(variantIds)
  if (key && map[key]) return map[key]

  for (const id of variantIds) {
    const singleKey = `[${id}]`
    if (map[singleKey]) return map[singleKey]
  }
  return null
}

export const getVariantInventoryQty = (inventory, variantIds) => {
  const entry = getInventoryEntry(inventory, variantIds)
  return entry ? parseInt(entry.qty, 10) || 0 : 0
}

export const getSelectedAvailableQty = (product, selections) => {
  if (!product || !hasVariantOptions(product)) return null
  if (!areRequiredOptionsSelected(product, selections)) return null
  return getVariantInventoryQty(product.inventory, getSelectedVariantIds(selections))
}

export const getSelectedVariantIds = (selections) => {
  return Object.values(selections || {}).filter(Boolean).map(Number)
}

export const getOptionId = (option) => option?.option_id ?? option?.id

export const getVariantId = (variant) => variant?.variant_id ?? variant?.id

export const areRequiredOptionsSelected = (product, selections) => {
  if (!hasVariantOptions(product)) return true
  return product.options
    .filter((opt) => parseFlag(opt.required))
    .every((opt) => {
      const optionId = getOptionId(opt)
      return selections[optionId] != null && selections[optionId] !== ''
    })
}

export const parseSalePriceRange = (range) => {
  if (!range || range === '') return null
  if (typeof range === 'object' && range.min != null && range.max != null) {
    return { min: parsePrice(range.min), max: parsePrice(range.max) }
  }
  if (typeof range === 'string') {
    try {
      const parsed = JSON.parse(range)
      if (parsed?.min != null && parsed?.max != null) {
        return { min: parsePrice(parsed.min), max: parsePrice(parsed.max) }
      }
    } catch (e) {
      return null
    }
  }
  return null
}

export const normalizeVariantCombination = (value) => {
  if (Array.isArray(value)) {
    return value.map(Number).filter(Boolean).sort((a, b) => a - b).join(',')
  }
  if (typeof value === 'string' && value) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return parsed.map(Number).filter(Boolean).sort((a, b) => a - b).join(',')
      }
    } catch (e) {
      return value.split(',').map(Number).filter(Boolean).sort((a, b) => a - b).join(',')
    }
  }
  return ''
}

export const variantCombinationKey = (variantIds) => {
  return [...(variantIds || [])].map(Number).filter(Boolean).sort((a, b) => a - b).join(',')
}

export const matchesVariantCombination = (itemCombination, variantIds) => {
  return normalizeVariantCombination(itemCombination) === variantCombinationKey(variantIds)
}

export const getListPriceInfo = (product) => {
  if (!product) {
    return { sellingPrice: 0, regularPrice: 0, isRange: false, min: 0, max: 0 }
  }

  const regularPrice = parsePrice(product.regular_price)

  if (hasVariantOptions(product)) {
    const range = parseSalePriceRange(product.sale_price_range)
    if (range) {
      return { sellingPrice: null, regularPrice, isRange: true, min: range.min, max: range.max }
    }
    return {
      sellingPrice: parsePrice(product.sale_price),
      regularPrice,
      isRange: false,
      min: 0,
      max: 0,
    }
  }

  return {
    sellingPrice: parsePrice(product.sale_price),
    regularPrice,
    isRange: false,
    min: 0,
    max: 0,
  }
}

export const getDetailPriceInfo = (product, selections) => {
  if (!product) {
    return { sellingPrice: 0, regularPrice: 0, isRange: false, canAdd: false, min: 0, max: 0 }
  }

  const regularPrice = parsePrice(product.regular_price)
  const variantIds = getSelectedVariantIds(selections)

  if (hasVariantOptions(product)) {
    if (!areRequiredOptionsSelected(product, selections)) {
      const range = parseSalePriceRange(product.sale_price_range)
      if (range) {
        return {
          sellingPrice: null,
          regularPrice,
          isRange: true,
          min: range.min,
          max: range.max,
          canAdd: false,
        }
      }
      return {
        sellingPrice: parsePrice(product.sale_price),
        regularPrice,
        isRange: false,
        canAdd: false,
        min: 0,
        max: 0,
      }
    }

    const entry = getInventoryEntry(product.inventory, variantIds)
    const sellingPrice = entry ? parsePrice(entry.price ?? entry.sale_price) : 0
    const availableQty = getVariantInventoryQty(product.inventory, variantIds)
    return {
      sellingPrice,
      regularPrice,
      isRange: false,
      canAdd: sellingPrice > 0 && availableQty > 0,
      availableQty,
      min: 0,
      max: 0,
    }
  }

  return {
    sellingPrice: parsePrice(product.sale_price),
    regularPrice,
    isRange: false,
    canAdd: true,
    min: 0,
    max: 0,
  }
}

export const parseVariantIds = (value) => {
  const key = normalizeVariantCombination(value)
  return key ? key.split(',').map(Number) : []
}

export const getMaxCartQuantity = (product, variantIds) => {
  if (product && hasVariantOptions(product) && variantIds?.length) {
    return getVariantInventoryQty(product.inventory, variantIds)
  }
  return null
}

export const showNoteField = (product) => parseFlag(product?.note_status)

export const canQuickAdd = (product) => {
  return !isProductUnavailable(product) && !hasRequiredOptions(product)
}
